// src/pages/Home/Home.js
import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import { usePublishedProjects } from '../../hooks/useProjects';
import { useSettings } from '../../hooks/useSettings';
import CachedImage from '../../components/CachedImage/CachedImage';

function Home() {
  const { projects, loading } = usePublishedProjects();
  const { settings } = useSettings();

  // Get the first project as the featured one (you can change the logic to feature any project)
  const featuredProject = projects.find(project => project.featured) || projects[0];

  return (
    <div className="home-container">
      <section className="hero-section">
        {/* Wireframe Robot Visualization */}
        <div className="hero-robot-container">
          {/* Robot wireframe head */}
          <div className="robot-wireframe">
            {/* Head outline */}
            <div className="robot-head">
              <div className="robot-head-top"></div>
              <div className="robot-head-side robot-head-left"></div>
              <div className="robot-head-side robot-head-right"></div>
            </div>
            {/* Eyes */}
            <div className="robot-eyes">
              <div className="robot-eye robot-eye-left">
                <div className="eye-glow"></div>
              </div>
              <div className="robot-eye robot-eye-right">
                <div className="eye-glow"></div>
              </div>
            </div>
            {/* Antenna */}
            <div className="robot-antenna">
              <div className="antenna-base"></div>
              <div className="antenna-tip"></div>
            </div>
            {/* Jaw/chin area */}
            <div className="robot-jaw">
              <div className="jaw-line jaw-line-1"></div>
              <div className="jaw-line jaw-line-2"></div>
              <div className="jaw-line jaw-line-3"></div>
            </div>
            {/* Circuit lines */}
            <div className="circuit-lines">
              <div className="circuit circuit-1"></div>
              <div className="circuit circuit-2"></div>
              <div className="circuit circuit-3"></div>
              <div className="circuit circuit-4"></div>
            </div>
            {/* Data nodes */}
            <div className="data-nodes">
              <div className="node node-1"></div>
              <div className="node node-2"></div>
              <div className="node node-3"></div>
              <div className="node node-4"></div>
            </div>
          </div>
          {/* Floating particles */}
          <div className="robot-particles">
            <span></span><span></span><span></span>
            <span></span><span></span><span></span>
          </div>
        </div>

        <div className="hero-content">
          {/* Portrait image - inline on mobile, absolute on desktop */}
          {settings.profileImageUrl && (
            <div className="hero-portrait">
              <img
                src={settings.profileImageUrl}
                alt="Carl Gathmann"
              />
              <div className="portrait-frame"></div>
            </div>
          )}
          <div className="hero-text">
            <h1>CARL GATHMANN</h1>
            <h2>Robotics & Autonomous Systems Engineer</h2>
            {/*Exploring robotics – one project, one idea, one line of code at a time.*/}
            <p>Passion for robotics, patience for the process.</p>
            <div className="hero-buttons">
              <Link to="/resume" className="primary-button" onClick={() => window.scrollTo(0, 0)}>View My Resume</Link>
              <Link to="/projects" className="secondary-button" onClick={() => window.scrollTo(0, 0)}>View My Projects</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="highlights-section">
        <h2 className="title-line line-primary">What I Do</h2>
        <div className="highlights-grid">
          <div className="highlight-card">
            <h3 className="subtitle-line line-blue">Robotics</h3>
            <p>This topic has become a meaningful part of my daily life — I've always had a fascination with things that move at the press of a button.</p>
            <Link to="/projects?filter=Robotics" className="highlight-link" onClick={() => window.scrollTo(0, 0)}>See robotics projects</Link>
          </div>
          <div className="highlight-card">
            <h3 className="subtitle-line line-warm">Programming</h3>
            <p>Building applications in Python, Java, and C++ with emphasis on clean architecture and efficient algorithms.</p>
          </div>
          <div className="highlight-card">
            <h3 className="subtitle-line line-cool">IoT</h3>
            <p>My fascination with smart home technology and IoT runs deep, which is why I've built several DIY hardware projects to expand the capabilities of my own smart home setup.</p>
            <Link to="/projects?filter=IoT" className="highlight-link" onClick={() => window.scrollTo(0, 0)}>Explore IoT projects</Link>
          </div>
        </div>
      </section>

      <section className="featured-project-section">
        <h2 className="title-line line-sunset">Featured Project</h2>
        {loading ? (
          <div className="loading">Loading featured project...</div>
        ) : featuredProject ? (
          <div className="featured-project">
              <CachedImage
                src={featuredProject.imageUrl.startsWith('http') ? featuredProject.imageUrl : (featuredProject.imageUrl.startsWith('/') ? featuredProject.imageUrl : `/${featuredProject.imageUrl}`)}
                thumbnailSrc={featuredProject.thumbnailUrl}
                alt={featuredProject.title}
                showFull
              />
            <div className="project-details">
              <h3>{featuredProject.title}</h3>
              <p>{featuredProject.description}</p>
              <div className="project-tags">
                {featuredProject.tags && featuredProject.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
              <Link to={`/projects/${featuredProject.id}`} className="view-project-button" onClick={() => window.scrollTo(0, 0)}>View Project Details</Link>
            </div>
          </div>
        ) : (
          <div className="no-featured-project">
            <p>No featured project available.</p>
          </div>
        )}
      </section>

      <section className="cta-section">
        <h2 className="title-line line-primary centered">Let's Work Together</h2>
        <p>I’m open to internships, student positions, and freelance projects.</p>
        <div className="cta-buttons">
          <Link to="/resume" className="primary-button" onClick={() => window.scrollTo(0, 0)}>View My Resume</Link>
          <Link to="/contact" className="secondary-button" onClick={() => window.scrollTo(0, 0)}>Contact Me</Link>
        </div>
      </section>
    </div>
  );
}

export default Home;